import axios from 'axios';

import {
  getLastViewportPracticeId,
  getViewportPracticeId,
  toViewportPath
} from 'lib/helpers/viewportPath';

const Axios = axios.create({
  baseURL: process.env.REACT_APP_BASE_API_URL,
  withCredentials: true
});

// Requests that never transmit a body — the practice id has to ride the query
// string instead, or it is silently dropped between axios and the network.
const BODYLESS_METHODS = new Set(['get', 'head']);

// Absolute URLs are someone else's host (signed S3 / Filestack links, whose
// signature covers the query string) — never append anything to them.
const isAbsoluteUrl = (url) => /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url || '');

const withPracticeIdParam = (config, practiceId) => {
  if (config.params?.practice_id == null) {
    config.params = { ...config.params, practice_id: practiceId };
  }
  return config;
};

let inFlight = 0;
let lastActivity = 0;
const settled = () => {
  inFlight = Math.max(0, inFlight - 1);
  lastActivity = Date.now();
};

Axios.interceptors.response.use(
  (response) => {
    settled();
    return response;
  },
  (error) => {
    settled();
    return Promise.reject(error);
  }
);

/**
 * Resolves once no request has been in flight for `quietMs` (a save that chains several requests
 * starts the next as the last returns), or after `timeoutMs` at the latest.
 *
 * @returns {Promise<boolean>} whether the requests settled before the timeout
 */
export const whenRequestsSettle = ({ quietMs = 300, timeoutMs = 5000 } = {}) =>
  new Promise((resolve) => {
    const started = Date.now();
    const check = () => {
      if (inFlight === 0 && Date.now() - lastActivity >= quietMs) return resolve(true);
      if (Date.now() - started >= timeoutMs) return resolve(false);
      return setTimeout(check, 50);
    };
    check();
  });

Axios.interceptors.request.use((config) => {
  inFlight += 1;
  lastActivity = Date.now();
  const practiceId = getViewportPracticeId();
  if (practiceId == null) return config;
  if (isAbsoluteUrl(config.url)) return config;

  const data = config.data;

  if (data instanceof FormData) {
    if (!data.has('practice_id')) {
      data.append('practice_id', practiceId);
    }
    return config;
  }

  const method = (config.method || 'post').toLowerCase();

  if (BODYLESS_METHODS.has(method)) return withPracticeIdParam(config, practiceId);

  if (data == null) {
    config.data = { practice_id: practiceId };
    return config;
  }

  const isPlainObject =
    typeof data === 'object' &&
    !Array.isArray(data) &&
    Object.getPrototypeOf(data) === Object.prototype;

  if (isPlainObject) {
    if (!data.practice_id) {
      config.data = { ...data, practice_id: practiceId };
    }
    return config;
  }

  return withPracticeIdParam(config, practiceId);
});

// Global safety net: any `window.open('/portal/...')` anywhere in the app is
// rewritten while an admin is inside the viewport. Call sites that build links
// from data (task relationships, activity logs) cannot be fixed individually.
// The native reference lives on `window` so a re-import can't capture the
// already-patched function and rewrite twice.
if (typeof window !== 'undefined' && !window.__myriadPortalOpenPatched) {
  window.__myriadNativeOpen = window.open.bind(window);
  window.open = (url, ...rest) =>
    window.__myriadNativeOpen(toViewportPath(url, getViewportPracticeId()), ...rest);
  window.__myriadPortalOpenPatched = true;
}

/**
 * Opens a URL with the viewport rewrite deliberately bypassed.
 *
 * The rewrite decides from the URL alone, so it cannot tell an accidental
 * `/portal/...` link from one that means to leave the viewport on purpose —
 * impersonation being the case that matters: it starts in the viewport and must
 * land in the real portal. Those call sites say so explicitly by using this.
 *
 * @param {string} url
 * @param {...any} rest - remaining window.open arguments (target, features)
 * @returns {Window|null}
 */
export const openOutsideViewport = (url, ...rest) =>
  (typeof window !== 'undefined' && window.__myriadNativeOpen
    ? window.__myriadNativeOpen
    : window.open)(url, ...rest);

export { getLastViewportPracticeId, getViewportPracticeId };

export default Axios;
